import { logger } from "@utilities/logger";
import { useCallback, useRef, useState } from "react";

import type { AssistantMessage, ChatMessage } from "./ChatAnimation";

export interface ConversationStep {
  content: AssistantMessage | string;
  delay?: number; // Delay before this message appears (in ms)
  type: "assistant" | "user";
}

export function sleep(ms: number, onTimeoutKey?: (key: NodeJS.Timeout) => void) {
  return new Promise(resolve => {
    const key = setTimeout(resolve, ms);
    if (onTimeoutKey) {
      onTimeoutKey(key);
    }
  });
}

export interface ConversationScript {
  autoStart?: boolean;
  id: string;
  loop?: boolean;
  steps: ConversationStep[];
}

// Custom function to handle user message with typing effect
export const addUserMessageWithTyping = (callbacks: {
  addMessage: (message: Omit<ChatMessage, "id" | "timestamp">) => Promise<ChatMessage>;
  addTimeoutKeyToQueue: (key: NodeJS.Timeout) => void;
  setCurrentInputValue: (value: string) => void;
  setFullMessage: (value: string | undefined) => void;
  setIsSending: (value: boolean) => void;
  setIsTyping: (value: boolean) => void;
}) => {
  return async (content: string) => {
    const { addMessage, addTimeoutKeyToQueue, setCurrentInputValue, setFullMessage, setIsSending, setIsTyping } =
      callbacks;
    try {
      setIsTyping(true);
      setFullMessage(content); // Set the full message immediately for screen readers

      // Type out the message character by character
      for (let i = 0; i <= content.length; i++) {
        setCurrentInputValue(content.slice(0, i));
        await sleep(50, addTimeoutKeyToQueue); // Typing speed
      }

      setIsTyping(false);

      // Wait half a second before sending
      await sleep(500, addTimeoutKeyToQueue);

      // Send animation
      setIsSending(true);
      await sleep(200, addTimeoutKeyToQueue);

      // Add message to chat and clear input
      addMessage({ type: "user", content });

      // Reset input with opacity easing
      setCurrentInputValue("");
      setFullMessage(undefined);
      setIsSending(false);
    } catch (error) {
      logger.error(error instanceof Error ? error : null, "Error in typing animation");
      // Reset state on error
      setIsTyping(false);
      setIsSending(false);
      setCurrentInputValue("");
      setFullMessage(undefined);
    }
  };
};

export function useChatAnimation(
  initialMessages: ChatMessage[] = [],
  scripts: ConversationScript[] = [],
  customUserMessageHandler?: (content: string) => Promise<void>,
) {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const currentScriptIndexRef = useRef(0);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const isPlayingRef = useRef(false);

  const addMessage = useCallback(async (message: Omit<ChatMessage, "id" | "timestamp">) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, newMessage]);

    // If it's a tool call, wait for it to complete
    if (typeof message.content === "object" && message.content.type === "toolCall") {
      const toolCallDuration = message.content.loadingDuration || 2000;
      const readingTime = 2000;

      await sleep(toolCallDuration + readingTime, addTimeoutKeyToQueue);
    } else {
      // Regular assistant message, short pause
      await sleep(1000, addTimeoutKeyToQueue);
    }
    return newMessage;
  }, []);

  const addUserMessage = useCallback(
    async (content: string) => {
      if (customUserMessageHandler) {
        await customUserMessageHandler(content);
      } else {
        return addMessage({ type: "user", content });
      }
    },
    [addMessage, customUserMessageHandler],
  );

  const addAssistantMessage = useCallback(
    (content: AssistantMessage) => {
      return addMessage({ type: "assistant", content });
    },
    [addMessage],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const stopCurrentScript = useCallback(() => {
    timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current = [];
    isPlayingRef.current = false;
  }, []);

  const addTimeoutKeyToQueue = (key: NodeJS.Timeout) => {
    timeoutRefs.current.push(key);
  };

  const playScript = useCallback(
    (script: ConversationScript) => {
      stopCurrentScript();
      if (script.steps.length === 0) return;
      isPlayingRef.current = true;

      let currentStepIndex = 0;

      const executeNextStep = async () => {
        if (!isPlayingRef.current || currentStepIndex >= script.steps.length) {
          // Script completed, move to next script
          if (isPlayingRef.current) {
            const nextIndex = (currentScriptIndexRef.current + 1) % scripts.length;
            currentScriptIndexRef.current = nextIndex;

            // Wait 2 seconds before starting exit animation
            const clearTimeout = setTimeout(() => {
              clearMessages();

              // Then wait a short time before starting next script
              const nextScriptTimeout = setTimeout(() => {
                if (isPlayingRef.current) {
                  playScript(scripts[nextIndex]);
                }
              }, 500);

              timeoutRefs.current.push(nextScriptTimeout);
            }, 2000);

            timeoutRefs.current.push(clearTimeout);
          }
          return;
        }

        const step = script.steps[currentStepIndex];
        const stepDelay = step.delay || 1500;

        // Wait for step delay
        await sleep(stepDelay, addTimeoutKeyToQueue);

        if (!isPlayingRef.current) return;

        // Execute the step
        if (step.type === "user") {
          await addUserMessage(step.content as string);
        } else {
          await addMessage(step);
        }

        if (!isPlayingRef.current) return;

        // Move to next step
        currentStepIndex++;
        executeNextStep();
      };

      // Start execution
      executeNextStep();
    },
    [addMessage, addUserMessage, stopCurrentScript, clearMessages],
  );

  // Start the first script automatically
  const startAutoRotation = useCallback(() => {
    const script = scripts[currentScriptIndexRef.current];
    if (!isPlayingRef.current && script) {
      playScript(script);
    }
  }, [playScript, scripts]);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    stopCurrentScript();
  }, [stopCurrentScript]);

  return {
    messages,
    isPlaying: isPlayingRef.current,
    addMessage,
    addUserMessage,
    addAssistantMessage,
    clearMessages,
    playScript,
    stopCurrentScript,
    startAutoRotation,
    cleanup,
  };
}
